import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API from "../api/axios";

const HIDDEN_PATHS = [
    "/login", "/register", "/forgot-password",
    "/reset-password", "/join", "/admin",
  ];

export default function SubscriptionPopup() {
  const [show, setShow] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
  if (HIDDEN_PATHS.some(path => location.pathname.startsWith(path))) return;

  const token = localStorage.getItem("token");
  if (!token) return;

  const alreadyShown = sessionStorage.getItem("popup_shown");
  if (alreadyShown) return;

  API.get("/subscriptions/trial-status")
    .then((res) => {
      const data = res.data;

      const shouldShow =
        data.status === "expired" ||
        data.status === "trial" ||
        (data.status === "subscribed" && data.expiring_soon);

      if (shouldShow) {
        setPlanInfo(data);
        setShow(true);
      }

      sessionStorage.setItem("popup_shown", "true");
    })
    .catch(() => {});
}, [location.pathname]);

  if (!show || !planInfo) return null;

  const calculateDaysLeft = (expiresAtStr, fallbackDays) => {
    if (fallbackDays !== null && fallbackDays !== undefined) return fallbackDays;
    if (!expiresAtStr) return null;
    try {
      const expires = new Date(expiresAtStr);
      const now = new Date();
      const days = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
      return Math.max(0, days);
    } catch {
      return null;
    }
  };

  const days = calculateDaysLeft(planInfo.expires_at, planInfo.days_left);
  const isExpired = planInfo.status === "expired" || days === 0 || (days !== null && days < 0);
  const isExpiringSoon = planInfo.status === "subscribed" && days !== null && days <= 7 && days > 0;
  const isTrial = planInfo.status === "trial" && days !== null && days > 0;
  const isSubscribed = planInfo.status === "subscribed" && days !== null && days > 0;

  // Colour logic:
  // - Expired: red
  // - Expiring soon (paid, <=7 days): orange/amber
  // - Trial: orange
  // - Active and healthy: green (won't show but kept for safety)
  const accentColor = isExpired
    ? "#e74c3c"
    : isExpiringSoon
    ? "#f5a623"
    : isTrial
    ? "#f5a623"
    : "#2ecc71";

  // More urgent red for 1 day left
  const urgentColor = days === 1 ? "#e74c3c" : accentColor;
  const finalColor = isExpiringSoon ? urgentColor : accentColor;

  const handleSubscribe = () => {
    setShow(false);
    navigate("/subscription");
  };

  const planLabel = planInfo.plan === "pro" ? "Pro" : "Basic";

 const getTitle = () => {
  if (isExpired) {
    return planInfo.plan === "free" ? "Trial Expired" : "Subscription Expired";
  }
  if (isExpiringSoon) return "Subscription Expiring Soon";
  if (isTrial) return "Days Left on Free Trial";
  return `${planLabel} Plan Active`;
};

  const getMessage = () => {
  if (isExpired) {
    if (planInfo.plan === "free") {
      return "Your free trial has ended. Subscribe now to continue using MartSense.";
    }
    return "Your subscription has expired. Renew now to restore full access to MartSense and keep your business running.";
  }
  if (isExpiringSoon) {
    const dayWord = days === 1 ? "day" : "days";
    return `Your ${planLabel} Plan expires in ${days} ${dayWord}. Renew now to keep your business running without interruption.`;
  }
  if (isTrial) {
    return `You have ${days !== null ? `${days} days` : "some time"} left on your free trial.`;
  }
  return `Your ${planLabel} plan is active${days !== null ? ` with ${days} days remaining` : ""}.`;
};

  const getCircleContent = () => {
    if (isExpired) return "!";
    if (isExpiringSoon) return days ?? "!";
    if (isTrial) return days ?? "?";
    return "✓";
  };

  return (
    <div style={s.overlay} onClick={() => setShow(false)}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setShow(false)} style={s.closeBtn}>✕</button>

        {/* Circle indicator */}
        <div style={{ ...s.circle, borderColor: finalColor }}>
          <span style={{ ...s.circleText, color: finalColor }}>
            {getCircleContent()}
          </span>
        </div>

        <h2 style={s.title}>{getTitle()}</h2>
        <p style={s.message}>{getMessage()}</p>

        {/* Show renew/subscribe button for expired, trial and expiring soon */}
        {(isExpired || isTrial || isExpiringSoon) && (
  <button
    onClick={handleSubscribe}
    style={{
      ...s.subscribeBtn,
      background: `linear-gradient(135deg, ${finalColor}, ${finalColor}cc)`,
    }}
  >
    {isExpired ? "Renew Now" : isExpiringSoon ? "Renew Now" : "Subscribe Now"}
  </button>
)}

        <button onClick={() => setShow(false)} style={s.laterBtn}>
          {isSubscribed && !isExpiringSoon ? "Continue to Dashboard" : "Remind Me Later"}
        </button>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
  },
  modal: {
    background: "#0f1a1a",
    border: "1px solid #1e3333",
    borderRadius: "20px",
    padding: "40px 32px",
    width: "100%",
    maxWidth: "380px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    position: "relative",
  },
  closeBtn: {
    position: "absolute",
    top: "16px",
    right: "16px",
    background: "none",
    border: "none",
    color: "#5a8a8a",
    fontSize: "20px",
    cursor: "pointer",
  },
  circle: {
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    border: "4px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "8px",
  },
  circleText: {
    fontSize: "40px",
    fontWeight: "800",
    fontFamily: "inherit",
  },
  title: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#e8f5f5",
    lineHeight: "1.3",
  },
  message: {
    fontSize: "15px",
    color: "#8ab0b0",
    lineHeight: "1.7",
    maxWidth: "300px",
  },
  subscribeBtn: {
    width: "100%",
    border: "none",
    borderRadius: "12px",
    padding: "16px",
    fontSize: "16px",
    fontWeight: "700",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "#0a0f0f",
    marginTop: "8px",
  },
  laterBtn: {
    background: "none",
    border: "none",
    color: "#5a8a8a",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    fontFamily: "inherit",
  },
};
